/**
 * NinesnDimes - Cart Max Quantity Validator
 * Prevents customers from adding more items to cart than available inventory
 * Reads max_quantity from product metafields
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    metafieldKey: 'custom.max_quantity',
    storageKey: 'nnd_cart_limits',
    debounceDelay: 300
  };

  /**
   * Get max quantity for a product variant from metafields
   */
  function getMaxQuantityFromMetafield(product) {
    if (!product || !product.metafields) return null;

    for (let metafield of product.metafields) {
      if (metafield.namespace === 'custom' && metafield.key === 'max_quantity') {
        return parseInt(metafield.value, 10);
      }
    }
    return null;
  }

  /**
   * Show error message to user
   */
  function showError(message) {
    // Remove existing error if present
    const existing = document.querySelector('[data-nnd-error-message]');
    if (existing) existing.remove();

    const errorDiv = document.createElement('div');
    errorDiv.setAttribute('data-nnd-error-message', 'true');
    errorDiv.style.cssText = `
      background-color: #fef2f2;
      border: 1px solid #fca5a5;
      border-radius: 6px;
      color: #7f1d1d;
      padding: 12px 16px;
      margin-bottom: 16px;
      font-weight: 500;
      font-size: 14px;
      display: flex;
      align-items: center;
      gap: 8px;
    `;

    errorDiv.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" style="flex-shrink: 0;">
        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
      </svg>
      <span>${message}</span>
    `;

    const formContainer = document.querySelector('form[action*="/cart/add"]') ||
                         document.querySelector('.product-form') ||
                         document.querySelector('[data-product-form]');

    if (formContainer) {
      formContainer.insertBefore(errorDiv, formContainer.firstChild);
      setTimeout(() => errorDiv.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
    }
  }

  /**
   * Validate quantity input
   */
  function validateQuantity(quantityInput, maxQuantity) {
    const currentValue = parseInt(quantityInput.value, 10) || 1;

    if (currentValue > maxQuantity) {
      quantityInput.value = maxQuantity;
      showError(`⚠️ Only ${maxQuantity} item${maxQuantity !== 1 ? 's' : ''} available in stock. Quantity adjusted.`);
      return false;
    }
    return true;
  }

  /**
   * Disable "Add to Cart" button and show message if over limit
   */
  function handleAddToCart(event, maxQuantity) {
    const form = event.target.closest('form');
    const quantityInput = form?.querySelector('input[name="quantity"], input[type="number"]');

    if (!quantityInput) return;

    const quantity = parseInt(quantityInput.value, 10) || 1;

    if (quantity > maxQuantity) {
      event.preventDefault();
      event.stopPropagation();
      quantityInput.value = maxQuantity;
      showError(`⚠️ Only ${maxQuantity} item${maxQuantity !== 1 ? 's' : ''} available. Please adjust quantity.`);
    }
  }

  /**
   * Setup quantity controls on product page
   */
  function setupProductPage(product, maxQuantity) {
    // Find quantity input
    const quantityInput = document.querySelector('input[name="quantity"], input[type="number"][data-quantity-input]');

    if (!quantityInput) return;

    // Set max attribute
    quantityInput.max = maxQuantity;
    quantityInput.setAttribute('data-max-quantity', maxQuantity);

    // Add validation on input change
    const validateHandler = () => {
      validateQuantity(quantityInput, maxQuantity);
    };

    quantityInput.addEventListener('change', validateHandler);
    quantityInput.addEventListener('blur', validateHandler);

    // Listen for increment/decrement buttons (for theme that uses +/- buttons)
    const decrementBtn = document.querySelector('[data-quantity-decrement], button[aria-label*="Decrease"], button[aria-label*="decrease"]');
    const incrementBtn = document.querySelector('[data-quantity-increment], button[aria-label*="Increase"], button[aria-label*="increase"]');

    if (incrementBtn) {
      incrementBtn.addEventListener('click', () => {
        setTimeout(validateHandler, 50);
      });
    }

    if (decrementBtn) {
      decrementBtn.addEventListener('click', () => {
        setTimeout(validateHandler, 50);
      });
    }

    // Hook into add-to-cart button
    const addToCartBtn = document.querySelector('button[name="add"], button[type="submit"][value="add-to-cart"], [data-add-to-cart]');
    if (addToCartBtn) {
      addToCartBtn.addEventListener('click', (e) => handleAddToCart(e, maxQuantity));
    }

    // Also hook the form's submit event
    const form = quantityInput.closest('form');
    if (form) {
      form.addEventListener('submit', (e) => {
        if (!validateQuantity(quantityInput, maxQuantity)) {
          e.preventDefault();
          e.stopPropagation();
        }
      });
    }

    // Show notice about quantity limit
    const notice = document.createElement('div');
    notice.setAttribute('data-nnd-notice', 'true');
    notice.style.cssText = `
      background-color: #fef3c7;
      border: 1px solid #fcd34d;
      border-radius: 6px;
      color: #78350f;
      padding: 10px 14px;
      margin-top: 12px;
      font-size: 13px;
      display: flex;
      align-items: center;
      gap: 8px;
    `;
    notice.innerHTML = `
      <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
      </svg>
      <span>Maximum ${maxQuantity} item${maxQuantity !== 1 ? 's' : ''} per order</span>
    `;

    const quantitySection = quantityInput.closest('.product-form__input-group') ||
                           quantityInput.closest('[data-quantity-section]') ||
                           quantityInput.parentElement;

    if (quantitySection && !quantitySection.querySelector('[data-nnd-notice]')) {
      quantitySection.appendChild(notice);
    }
  }

  /**
   * Initialize when Shopify product object is available
   */
  function init() {
    // Check for Shopify product object
    if (typeof Shopify === 'undefined' || typeof Shopify.theme === 'undefined') {
      console.warn('NinesnDimes Max Quantity: Shopify theme data not available');
      return;
    }

    // Try to get product from global Shopify object or meta tag
    let product = window.Shopify?.theme?.product || null;

    // Fallback: try to get from product JSON script tag
    if (!product) {
      const productScriptTag = document.querySelector('script[type="application/json"][data-product-json], script[type="application/ld+json"][data-product], #ProductJson');
      if (productScriptTag) {
        try {
          product = JSON.parse(productScriptTag.textContent);
        } catch (e) {
          console.error('NinesnDimes Max Quantity: Could not parse product JSON', e);
        }
      }
    }

    // Fallback: construct from page elements
    if (!product || !product.metafields) {
      const titleEl = document.querySelector('h1[data-product-title], .product__title, .product-title');
      if (titleEl) {
        // Try to extract from page data-attributes
        product = {
          metafields: []
        };
      }
    }

    if (!product) {
      console.warn('NinesnDimes Max Quantity: Could not find product data on page');
      return;
    }

    // Get max quantity from metafield
    const maxQuantity = getMaxQuantityFromMetafield(product);

    if (!maxQuantity) {
      console.warn('NinesnDimes Max Quantity: No max quantity metafield found for this product');
      return;
    }

    console.log('NinesnDimes Max Quantity: Setting max quantity to', maxQuantity);

    // Setup the page
    setupProductPage(product, maxQuantity);
  }

  /**
   * Run on document ready
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Also re-initialize on dynamic content loads (Shopify's variant selector changes)
  if (typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver((mutations) => {
      // Reinit if product form structure changes
      for (let mutation of mutations) {
        if (mutation.type === 'childList') {
          const hasFormChange = Array.from(mutation.addedNodes).some(node =>
            node.nodeType === 1 && (node.matches?.('form') || node.querySelector?.('form'))
          );
          if (hasFormChange) {
            setTimeout(init, 100);
            break;
          }
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
})();